/*!
 * TrueView for Shopify: product page runtime for snippets/trueview-product.liquid.
 * Plain ES2017, no build step, safe to include more than once.
 */
(function (global) {
  'use strict';

  var VERSION = '0.1.1';

  // ---------------------------------------------------------------------------------------------------------
  // Dimension parsing (pure functions)
  // ---------------------------------------------------------------------------------------------------------

  // Units the API accepts. mm is not one of them yet, so it is converted to cm.
  var METRIC_TO_CM = { mm: 0.1, cm: 1, m: 100 };

  var NUMBER_RE = /\d+(?:\.\d+)?/g;
  // Units never run into a following letter, except `x` (`160cmx230cm`).
  var UNIT_RE = /^\s*(millimet(?:er|re)s?|centimet(?:er|re)s?|met(?:er|re)s?|mm|cms|cm|m|feet|foot|ft|inches|inch|in|'|")(?![a-wyz])/i;
  // Inches after a feet unit: `5'3"`, `5ft 3in`, `5'3` (bare, only below 12), but not `5ft 10ft`.
  var INCHES_RE = /^\s*(\d+(?:\.\d+)?)\s*(inches|inch|in|")?(?![a-wyz\d.])(?!\s*(?:feet|foot|ft|'))/i;
  var SEPARATOR_RE = /^\s*(?:x|\*|by|times)\s*$/i;

  // Shape words let a single measure count as a size (`Ø 200 cm`, `Square 200cm`). Shape itself isn't reported yet.
  var ROUND_RE = /\b(?:round|circle|circular|diameter|dia|rond|ronde|rund|runde|runden|redondo|redonda|rotondo|rotonda)\b|[\u00d8\u00f8\u2300]/i;
  var SQUARE_RE = /\b(?:square|sq|carr\u00e9|carre|quadrat|cuadrado|quadrato)\b/i;

  function unitKey(raw) {
    var u = raw.toLowerCase();
    if (u === "'" || u === 'ft' || u === 'feet' || u === 'foot') return 'ft';
    if (u === '"' || u === 'in' || u === 'inch' || u === 'inches') return 'in';
    if (u === 'mm' || u.indexOf('millimet') === 0) return 'mm';
    if (u === 'cm' || u === 'cms' || u.indexOf('centimet') === 0) return 'cm';
    return 'm';
  }

  function isImperial(unit) {
    return unit === 'ft' || unit === 'in';
  }

  function round2(n) {
    return Math.round(n * 100) / 100;
  }

  function fmt(n) {
    return String(round2(n));
  }

  // 63 -> `5'3"`, 60 -> `5'`, 9 -> `9"`: the format the SDK accepts for `ftin`.
  function formatFtIn(totalInches) {
    var feet = Math.floor(round2(totalInches) / 12);
    var inches = round2(totalInches - feet * 12);
    if (!feet) return fmt(inches) + '"';
    return inches ? feet + "'" + fmt(inches) + '"' : feet + "'";
  }

  function normalise(text) {
    return String(text)
      .replace(/[\u00a0\u2009\u202f]/g, ' ')
      .replace(/[\u2018\u2019\u2032\u00b4`]/g, "'")
      .replace(/[\u201c\u201d\u2033]/g, '"')
      .replace(/''/g, '"')
      .replace(/[\u00d7\u2715\u2716]/g, 'x')
      // Width/height/length labels: `23Wx12H''`, `22W x 35.4H inches`.
      .replace(/(\d)\s*[whl](?![a-wyz])/gi, '$1')
      // European decimal comma: `3,5` -> `3.5`. Three digits after the comma is not a decimal (no
      // thousands separators are expected in sizes, and `160,230` is more likely a list).
      .replace(/(\d),(\d{1,2})(?!\d)/g, '$1.$2');
  }

  // Every number in the string with the unit written after it (if any). Feet followed by inches become one
  // measure. Returns [{ value, unit, inches, start, end }] where value is in `unit` and `inches` is set for
  // feet-and-inches measures (total inches).
  function tokenize(s) {
    var tokens = [];
    var m;
    NUMBER_RE.lastIndex = 0;
    while ((m = NUMBER_RE.exec(s))) {
      var start = m.index;
      var end = start + m[0].length;
      // Skip digits that are part of a word or code ("V2", "SKU123"). `x` is a separator (`160x230`).
      if (start > 0 && /[a-wyz]/i.test(s[start - 1])) continue;
      var token = { value: parseFloat(m[0]), unit: null, inches: null, start: start, end: end };
      var unit = UNIT_RE.exec(s.slice(end));
      if (unit) {
        token.unit = unitKey(unit[1]);
        token.end = end + unit[0].length;
        if (token.unit === 'ft') {
          var inch = INCHES_RE.exec(s.slice(token.end));
          if (inch && (inch[2] || parseFloat(inch[1]) < 12)) {
            token.inches = token.value * 12 + parseFloat(inch[1]);
            token.end += inch[0].length;
          }
        }
      }
      NUMBER_RE.lastIndex = token.end;
      tokens.push(token);
    }
    return tokens;
  }

  function toInches(token) {
    if (token.inches !== null) return token.inches;
    return token.unit === 'ft' ? token.value * 12 : token.value;
  }

  // Resolves two measures to one unit the API accepts. Returns null for a metric/imperial mix.
  function combine(a, b, defaultUnits) {
    var ua = a.unit || b.unit;
    var ub = b.unit || a.unit;
    var defaulted = false;
    if (!ua) {
      ua = ub = defaultUnits;
      defaulted = true;
    }
    var result;
    if (!isImperial(ua) && !isImperial(ub)) {
      if (ua === ub && ua !== 'mm') {
        result = { width: fmt(a.value), length: fmt(b.value), units: ua };
      } else {
        result = { width: fmt(a.value * METRIC_TO_CM[ua]), length: fmt(b.value * METRIC_TO_CM[ub]), units: 'cm' };
      }
      result.sourceUnits = ua === ub ? ua : 'cm';
      result.size = [a.value * METRIC_TO_CM[ua], b.value * METRIC_TO_CM[ub]];
    } else if (isImperial(ua) && isImperial(ub)) {
      var ta = { value: a.value, unit: ua, inches: a.inches };
      var tb = { value: b.value, unit: ub, inches: b.inches };
      if (ua === ub && a.inches === null && b.inches === null) {
        result = { width: fmt(a.value), length: fmt(b.value), units: ua };
      } else {
        result = { width: formatFtIn(toInches(ta)), length: formatFtIn(toInches(tb)), units: 'ftin' };
      }
      result.sourceUnits = result.units;
      result.size = [toInches(ta), toInches(tb)];
    } else {
      return null;
    }
    result.unitsDefaulted = defaulted;
    return result;
  }

  function parseSizeMap(value) {
    if (!value) return {};
    if (typeof value === 'object') return value;
    var map = {};
    String(value).split('|').forEach(function (entry) {
      var i = entry.indexOf(':');
      if (i > 0) map[entry.slice(0, i).trim().toLowerCase()] = entry.slice(i + 1).trim();
    });
    return map;
  }

  /**
   * Extracts width, length and units from a free-form size string.
   *
   * options: { defaultUnits: 'cm'|'m'|'mm'|'ft'|'in', pick: 'first'|'last'|'metric'|'imperial',
   *            orientation: 'as_is'|'swap'|'short_first'|'long_first',
   *            sizeMap: { small: '60x90cm' } | 'Small:60x90cm|Medium:120x170cm' }
   *
   * Returns { width, length, units, sourceUnits, unitsDefaulted, match } or null. width and length are
   * strings in the format the SDK expects for `units` (`5'3"` for ftin).
   */
  function parseDimensions(text, options) {
    if (text === null || text === undefined) return null;
    var opts = options || {};
    var defaultUnits = opts.defaultUnits && (METRIC_TO_CM[opts.defaultUnits] || isImperial(opts.defaultUnits))
      ? opts.defaultUnits
      : 'cm';

    var raw = String(text).trim();
    if (!raw) return null;
    var mapped = parseSizeMap(opts.sizeMap)[raw.toLowerCase()];
    var s = normalise(mapped || raw);
    var tokens = tokenize(s);
    if (!tokens.length) return null;

    // Pairs joined by a separator. A third dimension (`W x L x H`) is ignored.
    var candidates = [];
    for (var i = 0; i < tokens.length - 1; i++) {
      var a = tokens[i];
      var b = tokens[i + 1];
      if (!SEPARATOR_RE.test(s.slice(a.end, b.start))) continue;
      var pair = combine(a, b, defaultUnits);
      if (pair) {
        pair.match = s.slice(a.start, b.end);
        candidates.push(pair);
      }
      while (i + 2 < tokens.length && SEPARATOR_RE.test(s.slice(tokens[i + 1].end, tokens[i + 2].start))) i++;
      i++;
    }

    // No pair: a single dimension (round or square), only when the string has no words besides the measures
    // (`200 cm`, `20''-(50 cm)`) or has a shape word (`Ø 200 cm`, `Square 200cm`). A lone measure in other text
    // is too often something else (`Pile height 1cm`, `3 in stock`).
    if (!candidates.length) {
      var shapeWord = ROUND_RE.test(s) || SQUARE_RE.test(s);
      var rest = s;
      for (var r = tokens.length - 1; r >= 0; r--) rest = rest.slice(0, tokens[r].start) + ' ' + rest.slice(tokens[r].end);
      var onlyMeasures = !/[a-z]/i.test(rest);
      tokens.forEach(function (t) {
        if (!shapeWord && !onlyMeasures) return;
        var single = combine(t, t, defaultUnits);
        if (single) {
          single.match = s.slice(t.start, t.end);
          candidates.push(single);
        }
      });
    }
    if (!candidates.length) return null;

    var chosen = candidates[0];
    if (opts.pick === 'last') {
      chosen = candidates[candidates.length - 1];
    } else if (opts.pick === 'metric' || opts.pick === 'imperial') {
      var wantImperial = opts.pick === 'imperial';
      for (var c = 0; c < candidates.length; c++) {
        var imperial = candidates[c].units === 'ftin' || isImperial(candidates[c].units);
        if (imperial === wantImperial) {
          chosen = candidates[c];
          break;
        }
      }
    }

    var swap = opts.orientation === 'swap' ||
      (opts.orientation === 'short_first' && chosen.size[0] > chosen.size[1]) ||
      (opts.orientation === 'long_first' && chosen.size[0] < chosen.size[1]);
    return {
      width: swap ? chosen.length : chosen.width,
      length: swap ? chosen.width : chosen.length,
      units: chosen.units,
      sourceUnits: chosen.sourceUnits,
      unitsDefaulted: chosen.unitsDefaulted,
      match: chosen.match,
    };
  }

  /**
   * Which option (0-based) holds the size on this product, or -1: the first position in `sizeOption` (1-based,
   * default '1,2,3') where at least one variant's value parses. Decided once per product, so a `Sample` value
   * doesn't move it, and every variant reads the same position.
   *
   * variantOptions: [['Blue', '160x230'], ...], one array per variant; parseOptions as for parseDimensions.
   */
  function findSizeOption(variantOptions, sizeOption, parseOptions) {
    var positions = String(sizeOption || '1,2,3').split(',');
    for (var p = 0; p < positions.length; p++) {
      var index = parseInt(positions[p], 10) - 1;
      if (!(index >= 0)) continue;
      for (var v = 0; v < (variantOptions || []).length; v++) {
        if (variantOptions[v] && parseDimensions(variantOptions[v][index], parseOptions)) return index;
      }
    }
    return -1;
  }

  var api = {
    version: VERSION,
    parseDimensions: parseDimensions,
    parseSizeMap: parseSizeMap,
    findSizeOption: findSizeOption,
  };

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
    return;
  }
  if (global.TrueViewShopify) return; // already loaded by another snippet on this page
  global.TrueViewShopify = api;

  // ---------------------------------------------------------------------------------------------------------
  // Runtime: SDK loader, instances, variant tracking, button state, Theme Editor dialog
  // ---------------------------------------------------------------------------------------------------------

  var document = global.document;
  var LOG_PREFIX = '[TrueView Shopify]';

  var SDK_URLS = {
    production: 'https://embed.nvzn.io/trueview/v1/trueview.min.js',
    staging: 'https://staging-embed.nvzn.io/trueview/v1/trueview.min.js',
  };

  // The button attributes each service takes for the first and second measurement.
  var SERVICE_FIELDS = {
    'ai-rugs': ['data-product-width', 'data-product-length'],
    'ar-rugs': ['data-product-width', 'data-product-length'],
  };

  var instances = [];
  api.instances = instances;

  function warn() {
    console.warn.apply(console, [LOG_PREFIX].concat([].slice.call(arguments)));
  }

  function list(value) {
    return String(value || '')
      .split(',')
      .map(function (item) { return item.trim(); })
      .filter(Boolean);
  }

  function setAttr(el, name, value) {
    if (value === null || value === undefined || value === '' || value === false) el.removeAttribute(name);
    else el.setAttribute(name, value === true ? '' : String(value));
  }

  // Whole-word, case-insensitive match against option values (`Sample`, `Free sample`, but not `Samples`).
  function wordMatcher(words) {
    if (!words.length) return null;
    var escaped = words.map(function (w) { return w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); });
    return new RegExp('(?:^|[^a-z0-9\\u00c0-\\u024f])(?:' + escaped.join('|') + ')(?:$|[^a-z0-9\\u00c0-\\u024f])', 'i');
  }

  // --- SDK ------------------------------------------------------------------------------------------------

  var sdkRequested = false;

  // Loads the SDK once per page. The SDK reads data-api-key / data-log from its own script tag. If the store
  // already loads it, the key is passed with TrueView.init() instead.
  function loadSdk(config) {
    if (sdkRequested) return;
    sdkRequested = true;
    var init = function () {
      if (global.TrueView) global.TrueView.init({ apiKey: config.apiKey, log: !!config.log });
    };
    if (global.TrueView) return init();

    var src = config.sdkUrl || SDK_URLS[config.sdkEnv] || SDK_URLS.production;
    var existing = document.querySelector('script[src="' + src + '"]');
    if (existing) {
      existing.addEventListener('load', init);
      return;
    }
    var script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.setAttribute('data-api-key', config.apiKey);
    if (config.log) script.setAttribute('data-log', 'true');
    script.addEventListener('error', function () {
      console.error(LOG_PREFIX, 'Could not load the TrueView SDK from', src);
    });
    document.head.appendChild(script);
  }

  // --- Instances ------------------------------------------------------------------------------------------

  function createInstance(root) {
    var data;
    try {
      data = JSON.parse(root.querySelector('script[data-trueview-config]').textContent);
    } catch (err) {
      console.error(LOG_PREFIX, 'Could not read the snippet config', err);
      return null;
    }

    var config = data.config;
    var inst = {
      root: root,
      config: config,
      product: data.product,
      button: root.querySelector('[data-trueview-button]'),
      variants: {},
      selected: null,
      reason: null,
      stateKey: null,
      log: function () {
        if (config.log) console.log.apply(console, [LOG_PREFIX].concat([].slice.call(arguments)));
      },
    };

    if (!SERVICE_FIELDS[config.service]) {
      warn('Unknown service "' + config.service + '": using the ai-rugs dimension attributes.');
    }

    prepareVariants(inst, data.variants || []);

    var anyValid = Object.keys(inst.variants).some(function (id) { return inst.variants[id].valid; });
    if (!anyValid && !config.designMode) {
      inst.log('No variant has both an image and dimensions: removing the button for product', inst.product.id);
      root.remove();
      return null;
    }

    loadSdk(config);
    root.addEventListener('click', function (event) { onClick(inst, event); }, true);
    if (config.designMode) watchHiddenInEditor(inst);

    inst.log('Config', config);
    if (config.log && console.table) console.table(variantTable(inst));

    update(inst);
    return inst;
  }

  // Parses every variant once when the page loads.
  function prepareVariants(inst, variants) {
    var config = inst.config;
    var product = inst.product;
    var parseOptions = {
      defaultUnits: config.defaultUnits,
      orientation: config.orientation,
      pick: config.dimensionPick,
      sizeMap: config.sizeMap,
    };
    var sizeIndex = findSizeOption(
      variants.map(function (v) { return v.options; }),
      config.sizeOption,
      parseOptions
    );
    var sources = list(config.dimensionSource);
    var excluded = wordMatcher(list(config.excludeOptionValues));

    inst.sizeOptionIndex = sizeIndex;
    variants.forEach(function (v) {
      var dimensions = null;
      var dimensionSource = null;
      for (var i = 0; i < sources.length && !dimensions; i++) {
        var text =
          sources[i] === 'option' ? (sizeIndex >= 0 ? v.options[sizeIndex] : null) :
          sources[i] === 'variant_metafield' ? v.dimensionMetafield :
          sources[i] === 'product_metafield' ? product.dimensionMetafield :
          sources[i] === 'title' ? product.title :
          null;
        dimensions = typeof text === 'string' ? parseDimensions(text, parseOptions) : null;
        if (dimensions) dimensionSource = sources[i];
      }

      var reason =
        config.requireAvailable && !v.available ? 'unavailable' :
        excluded && v.options.some(function (o) { return excluded.test(o); }) ? 'excluded_option' :
        !v.image ? 'no_image' :
        !dimensions ? 'no_dimensions' :
        null;

      inst.variants[String(v.id)] = {
        id: String(v.id),
        title: v.title,
        sku: v.sku,
        available: v.available,
        options: v.options,
        image: v.image || null,
        imageSource: v.imageSource || null,
        dimensions: dimensions,
        dimensionSource: dimensionSource,
        valid: !reason,
        reason: reason,
      };
    });
  }

  function variantTable(inst) {
    return Object.keys(inst.variants).map(function (id) {
      var v = inst.variants[id];
      var d = v.dimensions || {};
      return {
        id: id,
        title: v.title,
        valid: v.valid,
        reason: v.reason || '',
        width: d.width,
        length: d.length,
        units: d.units,
        from: v.dimensionSource,
        match: d.match,
        image: v.imageSource,
      };
    });
  }

  // --- Variant tracking -----------------------------------------------------------------------------------

  function productForm(inst) {
    var scope = null;
    if (inst.config.scopeSelector) {
      scope = inst.root.closest(inst.config.scopeSelector) || document.querySelector(inst.config.scopeSelector);
    }
    scope = scope || inst.root.closest('.shopify-section, section');
    var selector = 'form[action*="/cart/add"]';
    return (scope && scope.querySelector(selector)) || document.querySelector(selector);
  }

  // The URL's ?variant= if it belongs to this product, then the product form's id field, then the initial variant.
  function selectedVariantId(inst) {
    var fromUrl = new URLSearchParams(global.location.search).get('variant');
    if (fromUrl && inst.variants[fromUrl]) return fromUrl;
    var form = productForm(inst);
    var field = form && form.querySelector('[name="id"]');
    if (field && field.value) return String(field.value);
    return String(inst.product.initialVariantId);
  }

  function update(inst) {
    if (!inst.root.isConnected) return;
    var id = selectedVariantId(inst);
    var variant = inst.variants[id] || null;
    var reason = variant ? variant.reason : 'unknown_variant';
    var key = id + ':' + reason;
    if (key === inst.stateKey) return;

    inst.stateKey = key;
    inst.selected = variant;
    inst.reason = reason;
    applyState(inst, variant, reason);
    inst.log('Variant', id, reason || 'ok', variant && variant.dimensions);
  }

  function applyState(inst, variant, reason) {
    var config = inst.config;
    var root = inst.root;
    var button = inst.button;
    root.setAttribute('data-trueview-state', reason ? 'disabled' : 'enabled');
    setAttr(root, 'data-trueview-reason', reason);
    if (!button) return;

    var fields = SERVICE_FIELDS[config.service] || SERVICE_FIELDS['ai-rugs'];
    var d = variant && variant.dimensions;
    setAttr(button, 'data-product-image', variant && variant.image);
    setAttr(button, fields[0], d && d.width);
    setAttr(button, fields[1], d && d.length);
    setAttr(button, 'data-units', d && d.units);
    setAttr(button, 'data-variant-id', variant && variant.id);
    setAttr(button, 'data-variant-name', variant && variant.title);
    setAttr(button, 'data-sku', variant && variant.sku);

    // In the Theme Editor the button always shows and is never disabled.
    var live = !config.designMode;
    setAttr(button, 'disabled', live && !!reason && config.invalidVariant === 'disable');
    root.hidden = live && !!reason && config.invalidVariant === 'hide';
  }

  var pending = [];

  // Themes change variants in different ways, so any change event, history update or back/forward triggers a
  // re-read. A second, later read catches themes that update the URL or form after an async fetch.
  function scheduleUpdate() {
    pending.forEach(clearTimeout);
    pending = [50, 500].map(function (delay) {
      return setTimeout(function () { instances.forEach(update); }, delay);
    });
  }

  function watchVariantChanges() {
    document.addEventListener('change', scheduleUpdate, true);
    global.addEventListener('popstate', scheduleUpdate);
    ['pushState', 'replaceState'].forEach(function (name) {
      var original = global.history[name];
      global.history[name] = function () {
        var result = original.apply(this, arguments);
        scheduleUpdate();
        return result;
      };
    });
  }

  // --- Clicks ---------------------------------------------------------------------------------------------

  // Runs in the capture phase on the wrapper, before the button's own handler inside its shadow DOM. In the Theme
  // Editor it shows our dialog instead of the preview; on the live store it only logs. The recipe raises no events:
  // the SDK raises `trueview:event` for every launch.
  function onClick(inst, event) {
    if (!inst.button || !inst.button.contains(event.target)) return;
    if (inst.config.designMode) {
      event.preventDefault();
      event.stopPropagation();
      showEditorDialog(inst);
      return;
    }
    inst.log('Opening preview', inst.selected);
  }

  // --- Theme Editor ---------------------------------------------------------------------------------------

  var REASON_TEXT = {
    unknown_variant: 'The selected options don’t match a variant of this product.',
    unavailable: 'This variant is sold out, and the button is set to hide on sold-out variants.',
    excluded_option: 'This variant is a sample or swatch, so the button is disabled for it.',
    no_image: 'No TrueView image was found for this variant.',
    no_dimensions: 'No dimensions could be read for this variant.',
  };

  var FILTER_TEXT = {
    filters: 'This product doesn’t match the product types, collections or tags the button is enabled for.',
    excluded_tag: 'This product has a tag that excludes it.',
    not_enabled: 'This product isn’t switched on by its TrueView metafield.',
    excluded_metafield: 'This product is excluded by its TrueView metafield.',
    unavailable: 'This product is sold out, and the button is set to hide on sold-out products.',
  };

  function editorStatus(inst) {
    var v = inst.selected;
    if (!inst.product.eligible) {
      return FILTER_TEXT[inst.product.ineligibleReason] + ' The button won’t show on this product on your live store.';
    }
    if (!inst.reason) {
      var d = v.dimensions;
      return 'This variant is ready: ' + d.width + ' × ' + d.length + ' ' + d.units + ', with an image.';
    }
    var anyValid = Object.keys(inst.variants).some(function (id) { return inst.variants[id].valid; });
    return REASON_TEXT[inst.reason] + (anyValid
      ? ' On your live store the button is disabled for this variant.'
      : ' No variant of this product is ready, so the button won’t show on this product on your live store.');
  }

  var dialog = null;

  function showEditorDialog(inst) {
    if (!dialog) dialog = buildDialog();
    dialog.opener = inst.button;
    dialog.querySelector('[data-status]').textContent = editorStatus(inst);
    dialog.showModal();
  }

  // Built on first use, inside a shadow root so theme CSS can't restyle it.
  function buildDialog() {
    var host = document.createElement('div');
    host.setAttribute('data-trueview-editor-dialog', '');
    document.body.appendChild(host);
    var shadow = host.attachShadow({ mode: 'open' });
    shadow.innerHTML =
      '<style>' +
      'dialog{max-width:420px;width:calc(100% - 32px);border:0;border-radius:12px;padding:24px;' +
      'font:14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;' +
      'color:#1e293b;background:#fff;box-shadow:0 20px 50px rgba(15,23,42,.25)}' +
      'dialog::backdrop{background:rgba(15,23,42,.45)}' +
      '.badge{display:inline-block;font-size:12px;font-weight:600;color:#6d28d9;background:#f3e8ff;' +
      'border-radius:999px;padding:2px 10px;margin-bottom:8px}' +
      'h2{font-size:18px;margin:0 0 8px}p{margin:0 0 12px}' +
      '.status{background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;padding:10px 12px}' +
      '.close{position:absolute;top:12px;right:12px;border:0;background:none;font-size:22px;line-height:1;' +
      'cursor:pointer;color:#64748b;padding:4px}' +
      '.ok{display:block;margin:16px 0 0 auto;border:0;border-radius:999px;padding:8px 20px;font:inherit;' +
      'font-weight:600;color:#fff;background:#1e293b;cursor:pointer}' +
      'button:focus-visible{outline:2px solid #7c3aed;outline-offset:2px}' +
      '</style>' +
      '<dialog aria-labelledby="t">' +
      '<button class="close" type="button" aria-label="Close" data-close>×</button>' +
      '<span class="badge">Theme Editor</span>' +
      '<h2 id="t">TrueView previews open on your live store</h2>' +
      '<p>The button shows on every product here so you can place and style it. Previews don’t open inside ' +
      'the Theme Editor.</p>' +
      '<p>To try it, preview your theme outside the editor and open a product that has a TrueView image and ' +
      'dimensions.</p>' +
      '<p class="status" data-status></p>' +
      '<button class="ok" type="button" data-close>Got it</button>' +
      '</dialog>';

    var el = shadow.querySelector('dialog');
    var close = function () { el.close(); };
    [].forEach.call(shadow.querySelectorAll('[data-close]'), function (b) { b.addEventListener('click', close); });
    el.addEventListener('click', function (event) {
      if (event.target === el) close(); // backdrop
    });
    el.addEventListener('close', function () {
      if (el.opener) el.opener.focus();
    });
    return el;
  }

  // The SDK hides the button when the service is unavailable (bad key, or this domain not on the allowlist).
  // In the Theme Editor, say so instead of leaving an empty space.
  function watchHiddenInEditor(inst) {
    if (!inst.button) return;
    new MutationObserver(function () {
      if (inst.button.getAttribute('data-hidden') !== 'true' || inst.root.querySelector('[data-trueview-note]')) return;
      var note = document.createElement('p');
      note.setAttribute('data-trueview-note', '');
      note.style.cssText = 'font-size:13px;padding:8px 12px;border:1px dashed #94a3b8;border-radius:8px;color:#475569';
      note.textContent = 'TrueView button hidden: the service isn’t available for this API key on this domain. ' +
        'Check the api_key and that this domain is on the store’s allowed domains. (Only shown in the Theme Editor.)';
      inst.root.appendChild(note);
    }).observe(inst.button, { attributes: true, attributeFilter: ['data-hidden'] });
  }

  // --- Start ----------------------------------------------------------------------------------------------

  // Sets up every snippet on the page that isn't set up yet. Runs at load and whenever the DOM changes, which
  // covers Theme Editor section reloads, themes that re-render the product section, and quick views.
  function init() {
    for (var i = instances.length - 1; i >= 0; i--) {
      if (!instances[i].root.isConnected) instances.splice(i, 1);
    }
    [].forEach.call(document.querySelectorAll('[data-trueview-product]:not([data-trueview-ready])'), function (root) {
      root.setAttribute('data-trueview-ready', '');
      var inst = createInstance(root);
      if (inst) instances.push(inst);
    });
  }
  api.init = init;

  function start() {
    init();
    watchVariantChanges();
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      setTimeout(function () {
        queued = false;
        init();
      }, 100);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})(typeof window !== 'undefined' ? window : this);
